export const REQUIRED_CONTROL_SITES = 10;
export const HISTAMINE_CV_LIMIT_PERCENT = 30;
export const NEGATIVE_WHEAL_LIMIT_MM = 3;
export const NEGATIVE_FLARE_LIMIT_MM = 10;

export interface DiameterPair {
  largest: number | null;
  perpendicular: number | null;
}

export interface NegativeControlSite {
  wheal: DiameterPair;
  flare: DiameterPair;
}

export type CalculationStatus =
  | "incomplete"
  | "invalid-input"
  | "cannot-determine"
  | "meets"
  | "does-not-meet";

export interface HistamineCalculation {
  status: CalculationStatus;
  completeSiteCount: number;
  siteMeans: Array<number | null>;
  mean: number | null;
  sampleStandardDeviation: number | null;
  coefficientOfVariationPercent: number | null;
}

export interface NegativeCalculation {
  status: CalculationStatus;
  completeSiteCount: number;
  siteMeans: Array<{ wheal: number | null; flare: number | null }>;
  failingSites: number[];
}

export interface ProficiencyCalculation {
  status: CalculationStatus;
  histamine: HistamineCalculation;
  negative: NegativeCalculation;
}

function validMeasurement(value: number | null): value is number {
  return value !== null && Number.isFinite(value) && value >= 0;
}

function invalidMeasurement(value: number | null) {
  return value !== null && (!Number.isFinite(value) || value < 0);
}

export function meanDiameter(pair: DiameterPair): number | null {
  if (!validMeasurement(pair.largest) || !validMeasurement(pair.perpendicular)) return null;
  return (pair.largest + pair.perpendicular) / 2;
}

export function sampleStandardDeviation(values: number[]) {
  if (values.length < 2) return null;
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const squaredDeviations = values.reduce((sum, value) => sum + (value - mean) ** 2, 0);
  return Math.sqrt(squaredDeviations / (values.length - 1));
}

export function meetsHistamineStandard(coefficientOfVariationPercent: number) {
  return coefficientOfVariationPercent < HISTAMINE_CV_LIMIT_PERCENT;
}

function calculateHistamine(sites: DiameterPair[]): HistamineCalculation {
  const hasInvalidInput = sites.some(
    (site) => invalidMeasurement(site.largest) || invalidMeasurement(site.perpendicular),
  );
  const siteMeans = sites.map(meanDiameter);
  const completeSiteCount = siteMeans.filter((value): value is number => value !== null).length;
  const base = {
    completeSiteCount,
    siteMeans,
    mean: null,
    sampleStandardDeviation: null,
    coefficientOfVariationPercent: null,
  };

  if (hasInvalidInput) return { ...base, status: "invalid-input" };
  if (sites.length !== REQUIRED_CONTROL_SITES || completeSiteCount !== REQUIRED_CONTROL_SITES) {
    return { ...base, status: "incomplete" };
  }

  const completeMeans = siteMeans as number[];
  const mean = completeMeans.reduce((sum, value) => sum + value, 0) / REQUIRED_CONTROL_SITES;
  const standardDeviation = sampleStandardDeviation(completeMeans);
  if (mean === 0 || standardDeviation === null) {
    return {
      ...base,
      status: "cannot-determine",
      mean,
      sampleStandardDeviation: standardDeviation,
    };
  }

  const coefficientOfVariationPercent = (standardDeviation / mean) * 100;
  return {
    status: meetsHistamineStandard(coefficientOfVariationPercent) ? "meets" : "does-not-meet",
    completeSiteCount,
    siteMeans,
    mean,
    sampleStandardDeviation: standardDeviation,
    coefficientOfVariationPercent,
  };
}

function calculateNegative(sites: NegativeControlSite[]): NegativeCalculation {
  const hasInvalidInput = sites.some((site) =>
    [site.wheal.largest, site.wheal.perpendicular, site.flare.largest, site.flare.perpendicular].some(
      invalidMeasurement,
    ),
  );
  const siteMeans = sites.map((site) => ({ wheal: meanDiameter(site.wheal), flare: meanDiameter(site.flare) }));
  const completeSiteCount = siteMeans.filter((site) => site.wheal !== null && site.flare !== null).length;
  const base = { completeSiteCount, siteMeans, failingSites: [] as number[] };

  if (hasInvalidInput) return { ...base, status: "invalid-input" };
  if (sites.length !== REQUIRED_CONTROL_SITES || completeSiteCount !== REQUIRED_CONTROL_SITES) {
    return { ...base, status: "incomplete" };
  }

  const failingSites = siteMeans.flatMap((site, index) =>
    site.wheal! >= NEGATIVE_WHEAL_LIMIT_MM || site.flare! >= NEGATIVE_FLARE_LIMIT_MM ? [index + 1] : [],
  );
  return {
    completeSiteCount,
    siteMeans,
    failingSites,
    status: failingSites.length === 0 ? "meets" : "does-not-meet",
  };
}

export function calculateProficiency(
  histamineSites: DiameterPair[],
  negativeSites: NegativeControlSite[],
): ProficiencyCalculation {
  const histamine = calculateHistamine(histamineSites);
  const negative = calculateNegative(negativeSites);
  const statuses = [histamine.status, negative.status];
  let status: CalculationStatus;

  if (statuses.includes("invalid-input")) status = "invalid-input";
  else if (statuses.includes("incomplete")) status = "incomplete";
  else if (statuses.includes("cannot-determine")) status = "cannot-determine";
  else if (statuses.every((value) => value === "meets")) status = "meets";
  else status = "does-not-meet";

  return { status, histamine, negative };
}
